import React from 'react'
import type { PublicTag } from 'apollo-gql'
import cn from 'classnames'

import { BreadcrumbHorizontalPadding } from 'components/Breadcrumbs/Breadcrumbs'
import Link from 'components/Link/Link'
import TagsBreadcrumbs from 'components/TagsBreadcrumbs/TagsBreadcrumbs'
import Typography from 'components/UI/Typography/Typography'

import { MEMMO_RED } from '@/constants'

import styles from './NavigationTagsBreadcrumbs.module.css'

type TagPillsProps = {
  tags: PublicTag[]
  paddingHorizontal?: BreadcrumbHorizontalPadding
}

const TagPills = ({ tags, paddingHorizontal }: TagPillsProps): JSX.Element => (
  <nav
    className={cn(styles.pillsWrapper, styles[paddingHorizontal])}
    data-testid="tag-children"
  >
    {tags.map(({ profileCollection }) => {
      const { slug, publicCmsItem } = profileCollection

      return (
        <Link key={slug} href={`/profiles/${slug}`}>
          <a
            className={styles.pill}
            style={{
              backgroundColor: publicCmsItem.color ?? MEMMO_RED,
            }}
          >
            <Typography.H5>
              <span
                className={styles.pillText}
                style={{
                  color:
                    publicCmsItem.color?.toLowerCase() === '#ffffff'
                      ? '#000'
                      : null,
                }}
              >
                {publicCmsItem.title}
              </span>
            </Typography.H5>
          </a>
        </Link>
      )
    })}
  </nav>
)

const byNumberOfProfiles = (tagA: PublicTag, tagB: PublicTag) => {
  const aTotal = tagA.profileCollection?.profileProducts?.totalCount
  const bTotal = tagB.profileCollection?.profileProducts?.totalCount

  return bTotal - aTotal
}

type NavigationTagsBreadcrumbsProps = {
  tag: PublicTag
  paddingHorizontal?: BreadcrumbHorizontalPadding
}

const NavigationTagsBreadcrumbs = ({
  tag,
  paddingHorizontal,
}: NavigationTagsBreadcrumbsProps): JSX.Element => {
  const { childrenTags = [], childrenHasPlacement = false } = tag

  const validChildrenTags = childrenTags.filter(
    ({ visibleInNavigation, profileCollection }) =>
      visibleInNavigation &&
      profileCollection?.profileProducts?.totalCount &&
      typeof profileCollection.slug !== 'undefined' &&
      typeof profileCollection.publicCmsItem?.title !== 'undefined',
  )

  if (!childrenHasPlacement) {
    validChildrenTags.sort(byNumberOfProfiles)
  }

  return (
    <>
      <TagsBreadcrumbs
        tag={tag}
        lastItemClickable
        paddingHorizontal={paddingHorizontal}
      />

      {!!validChildrenTags.length && (
        <TagPills
          tags={validChildrenTags}
          paddingHorizontal={paddingHorizontal}
        />
      )}
    </>
  )
}

export default NavigationTagsBreadcrumbs
